import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Button, Card, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { useDispatch } from 'react-redux';
import ClaimStateAlerts from '@rd-web-markets/shared/dist/claim/ClaimStateAlerts';
import { Loading } from '@rd-web-markets/shared/dist/util';
import { ChangeHighlighter } from '@rd-web-markets/shared/dist/util/ChangeHighlighter';
import { apiKey, getDefaultConfig } from '@rd-web-markets/shared/dist/util/tinyMceConfig';
import claimSoftwareConsumablesService from '../../services/claim_software_consumables.service';
import { ITConsumableRow, ITConsumablesHeader, ITConsumablesTotalsRow } from './';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useSelector } from 'react-redux';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
export const SoftwareConsumablesSection = _ref => {
  let {
    accountType,
    claim,
    setClaim,
    refreshClaim,
    showCostFragmentsTable,
    allProjects
  } = _ref;
  const [claimSoftwareConsumables, setClaimSoftwareConsumables] = useState(null);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const claimId = useMemo(() => claim?.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  useEffect(() => {
    async function getData() {
      try {
        if (claimId) {
          setClaimSoftwareConsumables(await claimSoftwareConsumablesService.all(claimId));
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    }
    getData();
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const software = claimSoftwareConsumables[index];
    if (software.id) {
      try {
        await claimSoftwareConsumablesService.delete(software);
        const softwares = await claimSoftwareConsumablesService.all(claim.id);
        setClaimSoftwareConsumables(softwares);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newSoftwares = [...claimSoftwareConsumables];
      newSoftwares.splice(index, 1);
      setClaimSoftwareConsumables(newSoftwares);
    }
  };
  const handleAdd = software => {
    setClaimSoftwareConsumables([...claimSoftwareConsumables, software]);
  };
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  const userUnauthorizedToEdit = !hasPermissions();
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !claimSoftwareConsumables) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimStateAlerts, {
    claim: claim
  }), /*#__PURE__*/React.createElement(Alert, {
    variant: "info"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "info"), " Please ensure that all costs were incurred between ", claim.from_date, " and ", claim.to_date, "."), /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "software",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  })), !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, claimSoftwareConsumables.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(ITConsumablesHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, claimSoftwareConsumables.map((software, index) => {
    return /*#__PURE__*/React.createElement(ITConsumableRow, {
      key: index,
      index: index,
      sortedClaimProjectReports: sortedClaimProjectReports,
      claim: claim,
      handleDelete: () => handleDelete(index),
      claimSoftwareConsumables: claimSoftwareConsumables,
      setClaimSoftwareConsumables: setClaimSoftwareConsumables,
      refreshClaim: refreshClaim,
      handleError: handleError,
      headerScrollPosition: headerScrollPosition,
      claimSoftwareConsumablesService: claimSoftwareConsumablesService,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(ITConsumablesTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && !showCostFragmentsTable && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_employee_rnd_apportionments: []
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "add"), " Add Row")))), showCostFragmentsTable && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::ClaimSoftwareConsumable",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  }), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-start"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "outline-primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/consumables`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_left"), " Back")), /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-end"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/cloud_computings`
  }, "Continue ", /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_right")))));
};